import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/projects/IPO/components/ui/button";
import { Badge } from "@/projects/IPO/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/projects/IPO/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/IPO/components/ui/tabs";

import IPOService from "@/services/IPO/APIs/IPOService";
import {
  ArrowLeft,
  TrendingUp,
  Calendar,
  IndianRupee,
  Users,
  Target,
  Clock,
  Building2,
  AlertCircle,
  BarChart3,
  ExternalLink,
  FileText,
  LineChart,
  ArrowUpRight,
  Shield,
  CheckCircle2,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart as RechartsLineChart,
  Line,
  Legend,
} from "recharts";
import { ProviderIcon } from "@/projects/IPO/components/ProviderIcon";
import { CommonModal } from "@/common/components/ui";
import OtpVerification from "@/projects/NPS/Stepper/pages/OtpVerification";
import { isAuthenticated } from "@/utils/auth/authUtils";
import { IPOApplicationDialog } from "./IPOApplicationDialog";

// Helper function to map API response to component format
const mapIPOToComponentFormat = (apiIpo: any): any => {
  const formatStatus = (status: string): string => {
    if (!status) {
      return "N/A";
    }
    const s = status.toLowerCase();
    if (s === "live") {
      return "Open";
    }
    if (s === "close") {
      return "Closed";
    }
    if (s === "upcoming") {
      return "Upcoming";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatIssueSize = (size: number | string | undefined): string => {
    if (!size) {
      return "N/A";
    }
    const num = typeof size === "string" ? parseFloat(size) : size;
    if (num > 10000000) {
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    }
    if (num > 100000) {
      return `₹${(num / 100000).toFixed(2)} L`;
    }
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const minPrice = parseFloat(apiIpo.minPrice ?? "0");
  const maxPrice = parseFloat(apiIpo.maxPrice ?? "0");
  const lotSize = apiIpo.lotSize ?? 0;

  return {
    id: apiIpo.id,
    companyName: apiIpo.name,
    companyShortName: apiIpo.symbol ?? apiIpo.name,
    logo: apiIpo.ipoLogo,
    type: apiIpo.nsE_SubType ?? "Main Board",
    status: formatStatus(apiIpo.ipoStatus ?? apiIpo.listing_status),
    iconType: "trending",
    priceRange: {
      min: minPrice,
      max: maxPrice,
    },
    lotSize: lotSize,
    minBidQuantity: apiIpo.minBidQuantity ?? lotSize,
    minInvestment: minPrice * lotSize,
    issueSize: formatIssueSize(apiIpo.issueSize),
    bidDates: {
      start: apiIpo.biddingStartDate ?? "",
      end: apiIpo.biddingEndDate ?? "",
    },
    allotmentDate: apiIpo.allotmentDate ?? "",
    refundDate: apiIpo.refund_Initiation ?? "",
    listingDate: apiIpo.ipoListingDate ?? "",
    creditDate: apiIpo.creditToDAccount ?? "",
    subscriptionRate: {
      qib: Number(apiIpo.qib) || 0,
      nii: Number(apiIpo.nii) || 0,
      retail: Number(apiIpo.rii) || 0,
      total: Number(apiIpo.total) || 0,
    },
    gmp: 0,
    aboutCompany: apiIpo.aboutCompany ?? "",
    industry: "Financial Services",
    nseSymbol: apiIpo.symbol,
    bseSymbol: apiIpo.exchange === "BSE" ? apiIpo.symbol : "",
    isin: apiIpo.isin,
    rhpLink: apiIpo.ipoDoc ?? "",
    drhpLink: "",
    registrar: apiIpo.nsE_Registrar ?? "N/A",
    exchange: apiIpo.exchange ?? "NSE & BSE",
    faceValue: apiIpo.faceValue ?? "10",
    issueType: apiIpo.issueType === "FP" ? "Fixed Price" : "Book Built",
    companyAddress: apiIpo.registered_office_address,
    financials: [
      {
        year: "2024",
        revenue: parseFloat(apiIpo.revenue3 ?? "0"),
        profit: parseFloat(apiIpo.totalProfit3 ?? "0"),
        assets: parseFloat(apiIpo.totalAssets3 ?? "0"),
      },
      {
        year: "2023",
        revenue: parseFloat(apiIpo.revenue2 ?? "0"),
        profit: parseFloat(apiIpo.totalProfit2 ?? "0"),
        assets: parseFloat(apiIpo.totalAssets2 ?? "0"),
      },
      {
        year: "2022",
        revenue: parseFloat(apiIpo.revenue1 ?? "0"),
        profit: parseFloat(apiIpo.totalProfit1 ?? "0"),
        assets: parseFloat(apiIpo.totalAssets1 ?? "0"),
      },
    ].filter(f => f.revenue > 0 || f.profit > 0),
    objectives: apiIpo.aboutCompany ? [apiIpo.aboutCompany] : [],
    strengths: [],
    risks: [],
    promoters: apiIpo.managingDirector ? [apiIpo.managingDirector] : [],
  };
};

const IPODetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ipo, setIpo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [isOtpOpen, setIsOtpOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchIPO = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await IPOService.getById(id);
        const mappedData = mapIPOToComponentFormat(response);
        setIpo(mappedData);
      } catch (error) {
        console.error("Error fetching IPO details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchIPO();
  }, [id]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) {
      return "N/A";
    }
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      // <ProductLayout>
      <div className="container mx-auto px-4 py-10 md:px-6">
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#0c4a6e]" />
        </div>
      </div>
      // </ProductLayout>
    );
  }

  if (!ipo) {
    return (
      // <ProductLayout>
      <div className="container mx-auto px-4 py-10 md:px-6">
        <div className="flex h-64 flex-col items-center justify-center gap-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <p className="text-xl font-semibold">IPO not found</p>
          <Button onClick={() => navigate("/ipo")}>Back to IPOs</Button>
        </div>
      </div>
      // </ProductLayout>
    );
  }

  // Subscription data for pie chart
  const subscriptionData = [
    {
      name: "QIB",
      value: ipo.subscriptionRate.qib ?? 0,
      color: "#14b8a6",
    },
    {
      name: "Retail",
      value: ipo.subscriptionRate.retail ?? 0,
      color: "#3b82f6",
    },
    {
      name: "NII",
      value: ipo.subscriptionRate.nii ?? 0,
      color: "#f59e0b",
    },
  ];

  // Get latest financial year data
  const latestFinancial =
    ipo.financials && ipo.financials.length > 0
      ? ipo.financials[ipo.financials.length - 1]
      : null;

  // Calculate growth rates
  const calculateGrowth = (current: number, previous: number) => {
    if (!previous || previous === 0) {
      return 0;
    }
    return ((current - previous) / previous) * 100;
  };

  const revenueGrowth =
    ipo.financials && ipo.financials.length >= 2
      ? calculateGrowth(
          ipo.financials[ipo.financials.length - 1].revenue,
          ipo.financials[ipo.financials.length - 2].revenue
        )
      : 0;

  const profitGrowth =
    ipo.financials && ipo.financials.length >= 2
      ? calculateGrowth(
          ipo.financials[ipo.financials.length - 1].profit,
          ipo.financials[ipo.financials.length - 2].profit
        )
      : 0;

  const profitMargin =
    latestFinancial && latestFinancial.revenue > 0
      ? (latestFinancial.profit / latestFinancial.revenue) * 100
      : 0;

  const ApplyNow = () => {
    if (isAuthenticated()) {
      setIsApplyOpen(true);
      // Reset states when opening for a new application session if needed,
      // but Dialog onOpenChange already handles some.
    } else {
      setIsOtpOpen(true);
    }
  };

  return (
    // <ProductLayout>
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50/50">
      <div className="container mx-auto px-4 py-8 md:px-6">
        {/* Header with Back Button */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/ipo")}
            className="rounded-xl border-slate-200 bg-white shadow-sm transition-all hover:border-[#0c4a6e] hover:bg-slate-50 hover:shadow-md"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-black tracking-tight text-slate-900">
            IPO Details
          </h1>
        </div>

        {/* Enhanced Hero Section - Matching Screenshot Design */}
        <div className="group relative mb-8 overflow-hidden rounded-[1rem] bg-gradient-to-r from-[#0c4a6e] via-[#075985] to-[#66A0CD] p-5 shadow-2xl">
          {/* Subtle Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
          </div>

          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
          <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
            <div className="flex items-start gap-5">
              <div className="mt-1 flex h-24 w-40 items-center justify-center overflow-hidden rounded-2xl border border-white/20 bg-white shadow-xl">
                {ipo.logo ? (
                  <img
                    src={ipo.logo}
                    alt={ipo.companyShortName}
                    className="h-full w-full object-contain p-4"
                  />
                ) : (
                  <ProviderIcon
                    iconType={ipo.iconType}
                    size={40}
                    className="text-[#0c4a6e]"
                  />
                )}
              </div>
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Badge className="border-0 bg-white/20 text-xs text-white backdrop-blur-sm">
                    {ipo.type}
                  </Badge>
                  <Badge
                    className={`border-0 text-xs ${
                      ipo.status === "Open"
                        ? "bg-green-500/90 text-white"
                        : ipo.status === "Upcoming"
                          ? "bg-amber-500/90 text-white"
                          : ipo.status === "Listed"
                            ? "bg-blue-500/90 text-white"
                            : "bg-gray-500/90 text-white"
                    }`}
                  >
                    {ipo.status === "Open" && (
                      <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-white" />
                    )}
                    {ipo.status}
                  </Badge>
                  {ipo.status === "Listed" && ipo.listingGain !== undefined && (
                    <Badge
                      className={`${ipo.listingGain >= 0 ? "bg-green-500/90" : "bg-red-500/90"} border-0 text-xs text-white`}
                    >
                      Listing Gain: {ipo.listingGain >= 0 ? "+" : ""}
                      {ipo.listingGain}%
                    </Badge>
                  )}
                </div>
                <h1 className="mb-1 text-xl font-bold text-white md:text-3xl">
                  {ipo.companyName}
                </h1>
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <p className="flex items-center gap-1.5 text-sm">
                    <Building2 className="h-4 w-4" />
                    {ipo.industry}
                  </p>
                  <div className="flex items-center gap-2 border-l border-white/20 pl-3">
                    <Badge
                      variant="outline"
                      className="border-white/30 text-[10px] text-white"
                    >
                      Symbol: {ipo.nseSymbol}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="border-white/30 text-[10px] text-white"
                    >
                      ISIN: {ipo.isin}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <Button
              size="lg"
              // disabled={ipo.status !== "Open"}
              onClick={() => ApplyNow()}
              className="finease-btn text-mdsa group relative animate-glow cursor-pointer bg-white px-8 py-6 font-bold text-secondary hover:bg-white/90"
            >
              <div className="absolute inset-0 cursor-pointer rounded-xl bg-white/20 opacity-0 transition-opacity group-hover:opacity-100" />
              {ipo.status === "Open" ? (
                <>
                  <Target className="mr-2 h-4 w-4 animate-pulse" />
                  Apply Now
                </>
              ) : (
                <span>{ipo.status}</span>
              )}
            </Button>
            <IPOApplicationDialog
              isOpen={isApplyOpen}
              setIsOpen={setIsApplyOpen}
              ipo={ipo}
            />
          </div>
        </div>

        {/* Enhanced Key Metrics Grid with Modern Cards */}
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {[
            {
              icon: IndianRupee,
              label: "MIN INV/LOT",
              value: `₹${ipo.minInvestment.toLocaleString()}`,
              color:
                "bg-gradient-to-br from-[#0c4a6e]/10 to-[#0c4a6e]/5 text-[#0c4a6e]",
              borderColor: "border-[#0c4a6e]/20",
              glow: "from-[#0c4a6e]/30",
            },
            {
              icon: Users,
              label: "LOT SIZE",
              value: `${ipo.lotSize} shares`,
              color:
                "bg-gradient-to-br from-blue-50 to-blue-100/50 text-blue-600",
              borderColor: "border-blue-200",
              glow: "from-blue-400/30",
            },
            {
              icon: Target,
              label: "PRICE RANGE",
              value: `₹${ipo.priceRange.min}-₹${ipo.priceRange.max}`,
              color:
                "bg-gradient-to-br from-emerald-50 to-emerald-100/50 text-emerald-600",
              borderColor: "border-emerald-200",
              glow: "from-emerald-400/30",
            },
            {
              icon: BarChart3,
              label: "ISSUE SIZE",
              value: ipo.issueSize,
              color:
                "bg-gradient-to-br from-amber-50 to-amber-100/50 text-amber-600",
              borderColor: "border-amber-200",
              glow: "from-amber-400/30",
            },
            {
              icon: Calendar,
              label: "BID STARTS",
              value: formatDate(ipo.bidDates.start),
              color:
                "bg-gradient-to-br from-purple-50 to-purple-100/50 text-purple-600",
              borderColor: "border-purple-200",
              glow: "from-purple-400/30",
            },
            {
              icon: Clock,
              label: "BID ENDS",
              value: formatDate(ipo.bidDates.end),
              color:
                "bg-gradient-to-br from-teal-50 to-teal-100/50 text-teal-600",
              borderColor: "border-teal-200",
              glow: "from-teal-400/30",
            },
          ].map(item => (
            <Card
              key={`key-metric-${item.label}`}
              className="group relative overflow-hidden border bg-white/80 shadow-sm backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:border-slate-300 hover:shadow-xl"
            >
              {/* Animated Glow Effect */}
              <div
                className={`absolute -right-4 -top-4 h-16 w-16 rounded-full bg-gradient-to-br ${item.glow} to-transparent opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100`}
              />

              {/* Shine Effect */}
              <div className="absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 group-hover:translate-x-[100%]" />

              <CardContent className="relative p-5">
                <div
                  className={`mb-4 w-fit rounded-2xl p-3 ${item.color} border ${item.borderColor} shadow-sm transition-all duration-300 group-hover:rotate-3 group-hover:scale-110 group-hover:shadow-md`}
                >
                  <item.icon className="h-6 w-6" />
                </div>
                <p className="mb-2 text-[10px] font-black uppercase tracking-[0.15em] text-slate-500">
                  {item.label}
                </p>
                <p className="text-base font-black leading-tight text-slate-900">
                  {item.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Subscription Status & Timeline Grid */}
        <div className="mb-8 grid gap-6 lg:grid-cols-3">
          {/* Subscription Status Card - Enhanced */}
          <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl lg:col-span-2">
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#66A0CD]/5 via-transparent to-blue-500/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

            <CardHeader className="relative pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                  <div className="rounded-xl bg-gradient-to-br from-[#66A0CD] to-emerald-600 p-2 shadow-lg">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  Subscription Status
                </CardTitle>
                <Badge
                  variant="outline"
                  className="border-slate-200 bg-slate-50 text-xs font-semibold"
                >
                  <Clock className="mr-1 h-3 w-3" />
                  As of {formatDate(ipo.bidDates.end)}, 3:00 PM
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="space-y-5">
                  {[
                    {
                      label: "Qualified Institutional Buyers (QIB)",
                      value: ipo.subscriptionRate.qib ?? 0,
                      color: "bg-gradient-to-r from-[#66A0CD] to-emerald-500",
                      textColor: "text-emerald-600",
                    },
                    {
                      label: "Retail Individual Investor (RII)",
                      value: ipo.subscriptionRate.retail ?? 0,
                      color: "bg-gradient-to-r from-blue-500 to-blue-600",
                      textColor: "text-blue-600",
                    },
                    {
                      label: "Non-Institutional Investor (NII)",
                      value: ipo.subscriptionRate.nii ?? 0,
                      color: "bg-gradient-to-r from-amber-500 to-orange-500",
                      textColor: "text-amber-600",
                    },
                  ].map(item => (
                    <div
                      key={`subscription-${item.label}`}
                      className="space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-600">
                          {item.label}
                        </span>
                        <span
                          className={`text-lg font-black ${item.textColor}`}
                        >
                          {item.value.toFixed(2)}x
                        </span>
                      </div>
                      <div className="relative h-3 overflow-hidden rounded-full bg-slate-100 shadow-inner">
                        <div
                          className={`h-full ${item.color} rounded-full shadow-sm transition-all duration-1000 ease-out`}
                          style={{
                            width: `${Math.min(item.value * 10, 100)}%`,
                          }}
                        >
                          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-xl border-2 border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4 shadow-sm">
                    <span className="text-base font-bold text-slate-700">
                      Total Subscription
                    </span>
                    <span className="bg-gradient-to-r from-[#66A0CD] to-emerald-600 bg-clip-text text-3xl font-black text-transparent">
                      {ipo.subscriptionRate.total.toFixed(2)}x
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="relative h-[220px] w-full">
                    {/* Chart Container with Glow */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-slate-50 to-white shadow-inner" />
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <defs>
                          <filter id="glow">
                            <feGaussianBlur
                              stdDeviation="3"
                              result="coloredBlur"
                            />
                            <feMerge>
                              <feMergeNode in="coloredBlur" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>
                        <Pie
                          data={subscriptionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={4}
                          dataKey="value"
                          stroke="white"
                          strokeWidth={3}
                        >
                          {subscriptionData.map(entry => (
                            <Cell
                              key={`cell-${entry.name}`}
                              fill={entry.color}
                              style={{ filter: "url(#glow)" }}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                            padding: "12px",
                          }}
                          formatter={(value: number | undefined) => [
                            `${value?.toFixed(2)}x`,
                            "",
                          ]}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                    {/* Legend */}
                    <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-4 pt-2">
                      {subscriptionData.map(entry => (
                        <div
                          key={`legend-${entry.name}`}
                          className="flex items-center gap-1.5"
                        >
                          <div
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-[10px] font-semibold text-slate-600">
                            {entry.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* IPO Timeline Card - Enhanced */}
          <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl">
            {/* Decorative gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#175d80]/5 via-transparent to-blue-500/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

            <CardHeader className="relative pb-3">
              <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                <div className="rounded-xl bg-gradient-to-br from-[#175d80] to-blue-600 p-2 shadow-lg">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                IPO Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <div className="space-y-5">
                {[
                  {
                    label: "Open Date",
                    date: formatDate(ipo.bidDates.start),
                    status: "completed",
                    icon: "✓",
                  },
                  {
                    label: "Close Date",
                    date: formatDate(ipo.bidDates.end),
                    status:
                      ipo.status === "Closed" || ipo.status === "Listed"
                        ? "completed"
                        : "active",
                    icon: ipo.status === "Open" ? "●" : "✓",
                  },
                  {
                    label: "Allotment",
                    date: formatDate(ipo.allotmentDate),
                    status: ipo.status === "Listed" ? "completed" : "pending",
                    icon: ipo.status === "Listed" ? "✓" : "○",
                  },
                  {
                    label: "Refund",
                    date: formatDate(ipo.refundDate),
                    status: ipo.status === "Listed" ? "completed" : "pending",
                    icon: ipo.status === "Listed" ? "✓" : "○",
                  },
                  {
                    label: "Listing",
                    date: formatDate(ipo.listingDate),
                    status: ipo.status === "Listed" ? "completed" : "pending",
                    icon: ipo.status === "Listed" ? "✓" : "○",
                  },
                ].map((item, i) => (
                  <div
                    key={`timeline-${item.label}`}
                    className="group/item flex items-center gap-4 transition-all hover:translate-x-1"
                  >
                    <div className="relative flex items-center justify-center">
                      {/* Connecting Line */}
                      {i < 4 && (
                        <div
                          className={`absolute left-1/2 top-6 h-8 w-0.5 -translate-x-1/2 ${
                            item.status === "completed"
                              ? "bg-gradient-to-b from-green-400 to-green-200"
                              : "bg-slate-200"
                          }`}
                        />
                      )}
                      {/* Status Circle */}
                      <div
                        className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                          item.status === "completed"
                            ? "border-green-400 bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/30"
                            : item.status === "active"
                              ? "animate-pulse border-amber-400 bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/30"
                              : "border-slate-300 bg-white shadow-sm"
                        }`}
                      >
                        <span
                          className={`text-xs font-black ${
                            item.status === "completed"
                              ? "text-white"
                              : item.status === "active"
                                ? "text-white"
                                : "text-slate-400"
                          }`}
                        >
                          {item.icon}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-1 items-center justify-between rounded-lg bg-slate-50/50 px-4 py-2.5 transition-all group-hover/item:bg-slate-100/50">
                      <span className="text-sm font-semibold text-slate-600">
                        {item.label}
                      </span>
                      <span className="text-sm font-bold text-slate-900">
                        {item.date}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Tabs Section */}
        <Tabs defaultValue="financials" className="mb-8">
          <TabsList className="mb-8 h-auto flex-wrap rounded-2xl border border-slate-200 bg-slate-100/80 p-1.5 shadow-sm backdrop-blur-sm">
            <TabsTrigger
              value="financials"
              className="rounded-xl px-5 py-2.5 font-semibold transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-[#0c4a6e] data-[state=active]:shadow-md"
            >
              <BarChart3 className="mr-2 h-4 w-4" />
              Financials
            </TabsTrigger>
            <TabsTrigger
              value="company"
              className="rounded-xl px-5 py-2.5 font-semibold transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-[#0c4a6e] data-[state=active]:shadow-md"
            >
              <Building2 className="mr-2 h-4 w-4" />
              Company Analysis
            </TabsTrigger>
            <TabsTrigger
              value="details"
              className="rounded-xl px-5 py-2.5 font-semibold transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-[#0c4a6e] data-[state=active]:shadow-md"
            >
              <FileText className="mr-2 h-4 w-4" />
              IPO Details
            </TabsTrigger>
            <TabsTrigger
              value="lotsize"
              className="rounded-xl px-5 py-2.5 font-semibold transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-[#0c4a6e] data-[state=active]:shadow-md"
            >
              <Target className="mr-2 h-4 w-4" />
              Market Lot
            </TabsTrigger>
            <TabsTrigger
              value="documents"
              className="rounded-xl px-5 py-2.5 font-semibold transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-[#0c4a6e] data-[state=active]:shadow-md"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Documents
            </TabsTrigger>
          </TabsList>

          {/* Financials Tab */}
          <TabsContent value="financials" className="space-y-6">
            {latestFinancial && (
              <>
                {/* Enhanced Financial Highlights */}
                <div className="grid gap-5 md:grid-cols-4">
                  {[
                    {
                      label: "Revenue (FY24)",
                      value: `₹${(latestFinancial.revenue / 100).toFixed(0)} Cr`,
                      growth:
                        revenueGrowth > 0
                          ? `+${revenueGrowth.toFixed(1)}% CAGR`
                          : "N/A",
                      color: "from-emerald-50 to-green-50",
                      iconColor: "text-emerald-600",
                      borderColor: "border-emerald-200",
                      icon: TrendingUp,
                      gradient: "from-emerald-500 to-green-600",
                    },
                    {
                      label: "Net Profit (FY24)",
                      value: `₹${(latestFinancial.profit / 100).toFixed(0)} Cr`,
                      growth:
                        profitGrowth > 0
                          ? `+${profitGrowth.toFixed(1)}%`
                          : "N/A",
                      color: "from-blue-50 to-indigo-50",
                      iconColor: "text-blue-600",
                      borderColor: "border-blue-200",
                      icon: BarChart3,
                      gradient: "from-blue-500 to-indigo-600",
                    },
                    {
                      label: "Total Assets",
                      value: `₹${(latestFinancial.assets / 100).toFixed(0)} Cr`,
                      growth: "As of FY2024",
                      color: "from-amber-50 to-orange-50",
                      iconColor: "text-amber-600",
                      borderColor: "border-amber-200",
                      icon: Building2,
                      gradient: "from-amber-500 to-orange-600",
                    },
                    {
                      label: "Profit Margin",
                      value: `${profitMargin.toFixed(1)}%`,
                      growth: "Net Profit / Revenue",
                      color: "from-purple-50 to-pink-50",
                      iconColor: "text-purple-600",
                      borderColor: "border-purple-200",
                      icon: Target,
                      gradient: "from-purple-500 to-pink-600",
                    },
                  ].map(item => (
                    <Card
                      key={`financial-highlight-${item.label}`}
                      className={`group relative overflow-hidden border bg-gradient-to-br ${item.color} shadow-md transition-all duration-500 hover:scale-105 hover:shadow-xl`}
                    >
                      {/* Animated background gradient */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 transition-opacity duration-500 group-hover:opacity-100`}
                      />

                      <CardContent className="relative p-6">
                        <div
                          className={`mb-4 w-fit rounded-2xl bg-gradient-to-br ${item.gradient} p-3 shadow-lg transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110`}
                        >
                          <item.icon className="h-6 w-6 text-white" />
                        </div>
                        <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-500">
                          {item.label}
                        </p>
                        <p className="mb-2 text-2xl font-black leading-tight text-slate-900">
                          {item.value}
                        </p>
                        <p className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                          {revenueGrowth > 0 && (
                            <TrendingUp className="h-3 w-3" />
                          )}
                          {item.growth}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Enhanced Charts Grid */}
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Revenue & Profit Trend - Enhanced */}
                  {ipo.financials && ipo.financials.length > 0 && (
                    <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-2xl">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-blue-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      <CardHeader className="relative">
                        <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                          <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-2 shadow-md">
                            <BarChart3 className="h-5 w-5 text-white" />
                          </div>
                          Revenue & Profit Trend
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="relative">
                        <div className="h-[320px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={ipo.financials}
                              margin={{
                                top: 20,
                                right: 20,
                                bottom: 20,
                                left: 0,
                              }}
                            >
                              <defs>
                                <linearGradient
                                  id="colorRevenue"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#14b8a6"
                                    stopOpacity={0.8}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#14b8a6"
                                    stopOpacity={0.2}
                                  />
                                </linearGradient>
                                <linearGradient
                                  id="colorProfit"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#3b82f6"
                                    stopOpacity={0.8}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#3b82f6"
                                    stopOpacity={0.2}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                                vertical={false}
                              />
                              <XAxis
                                dataKey="year"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                  fill: "#64748b",
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                  fill: "#64748b",
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "white",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "12px",
                                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                                  padding: "12px",
                                }}
                              />
                              <Bar
                                dataKey="revenue"
                                fill="url(#colorRevenue)"
                                radius={[12, 12, 0, 0]}
                                name="Revenue"
                              />
                              <Bar
                                dataKey="profit"
                                fill="url(#colorProfit)"
                                radius={[12, 12, 0, 0]}
                                name="Profit"
                              />
                              <Legend
                                wrapperStyle={{ paddingTop: "20px" }}
                                iconType="circle"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Financial Growth Trajectory - Enhanced */}
                  {ipo.financials && ipo.financials.length > 0 && (
                    <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-2xl">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-transparent to-blue-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      <CardHeader className="relative">
                        <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                          <div className="rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 p-2 shadow-md">
                            <LineChart className="h-5 w-5 text-white" />
                          </div>
                          Financial Growth Trajectory
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="relative">
                        <div className="h-[320px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsLineChart
                              data={ipo.financials}
                              margin={{
                                top: 20,
                                right: 20,
                                bottom: 20,
                                left: 0,
                              }}
                            >
                              <defs>
                                <linearGradient
                                  id="lineRevenue"
                                  x1="0"
                                  y1="0"
                                  x2="1"
                                  y2="0"
                                >
                                  <stop offset="0%" stopColor="#14b8a6" />
                                  <stop offset="100%" stopColor="#10b981" />
                                </linearGradient>
                                <linearGradient
                                  id="lineAssets"
                                  x1="0"
                                  y1="0"
                                  x2="1"
                                  y2="0"
                                >
                                  <stop offset="0%" stopColor="#8b5cf6" />
                                  <stop offset="100%" stopColor="#7c3aed" />
                                </linearGradient>
                                <linearGradient
                                  id="lineNetWorth"
                                  x1="0"
                                  y1="0"
                                  x2="1"
                                  y2="0"
                                >
                                  <stop offset="0%" stopColor="#3b82f6" />
                                  <stop offset="100%" stopColor="#2563eb" />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                              />
                              <XAxis
                                dataKey="year"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                  fill: "#64748b",
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                  fill: "#64748b",
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "white",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "12px",
                                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                                  padding: "12px",
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="revenue"
                                stroke="url(#lineRevenue)"
                                strokeWidth={4}
                                dot={{
                                  fill: "#14b8a6",
                                  r: 6,
                                  strokeWidth: 2,
                                  stroke: "white",
                                }}
                                activeDot={{ r: 8 }}
                                name="Revenue"
                              />
                              <Line
                                type="monotone"
                                dataKey="assets"
                                stroke="url(#lineAssets)"
                                strokeWidth={4}
                                dot={{
                                  fill: "#8b5cf6",
                                  r: 6,
                                  strokeWidth: 2,
                                  stroke: "white",
                                }}
                                activeDot={{ r: 8 }}
                                name="Total Assets"
                              />
                              <Line
                                type="monotone"
                                dataKey="netWorth"
                                stroke="url(#lineNetWorth)"
                                strokeWidth={4}
                                dot={{
                                  fill: "#3b82f6",
                                  r: 6,
                                  strokeWidth: 2,
                                  stroke: "white",
                                }}
                                activeDot={{ r: 8 }}
                                name="Net Worth"
                              />
                              <Legend
                                wrapperStyle={{ paddingTop: "20px" }}
                                iconType="line"
                              />
                            </RechartsLineChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </>
            )}
            {(!latestFinancial || ipo.financials.length === 0) && (
              <Card className="border-0 bg-white shadow-sm">
                <CardContent className="py-16 text-center">
                  <BarChart3 className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
                  <p className="text-muted-foreground">
                    Financial data not available
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Enhanced Company Analysis Tab */}
          <TabsContent value="company" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-indigo-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                    <div className="rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 p-2 shadow-md">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    Company Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <div className="space-y-4">
                    {[
                      { label: "Industry", value: ipo.industry ?? "N/A" },
                      {
                        label: "Company Address",
                        value: ipo.companyAddress ?? "N/A",
                      },
                      {
                        label: "Company Phone",
                        value: ipo.companyPhone ?? "N/A",
                      },
                      {
                        label: "Company Email",
                        value: ipo.companyEmail ?? "N/A",
                      },
                    ].map(item => (
                      <div
                        key={`company-info-${item.label}`}
                        className="group/item flex items-start justify-between rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-3 text-sm transition-all last:border-0 hover:border-slate-200 hover:bg-white hover:shadow-sm"
                      >
                        <span className="font-semibold text-slate-600">
                          {item.label}
                        </span>
                        <span className="max-w-[60%] text-right font-bold text-slate-900">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-teal-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                    <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-2 shadow-md">
                      <Shield className="h-5 w-5 text-white" />
                    </div>
                    About Company
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <p className="text-sm leading-relaxed text-slate-700">
                    {ipo.aboutCompany ?? "No description available."}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced Objectives, Strengths, Risks */}
            {ipo.objectives && ipo.objectives.length > 0 && (
              <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-[#66A0CD]/5 via-transparent to-emerald-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                    <div className="rounded-xl bg-gradient-to-br from-[#66A0CD] to-emerald-600 p-2 shadow-md">
                      <Target className="h-5 w-5 text-white" />
                    </div>
                    IPO Objectives
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <ul className="space-y-3">
                    {ipo.objectives.map((obj: string) => (
                      <li
                        key={`objective-${obj.substring(0, 20)}`}
                        className="group/item flex items-start gap-3 rounded-xl border border-slate-100 bg-gradient-to-r from-slate-50 to-white p-4 text-sm transition-all hover:border-[#66A0CD]/30 hover:bg-gradient-to-r hover:from-emerald-50/50 hover:to-white hover:shadow-sm"
                      >
                        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#66A0CD] to-emerald-600 shadow-sm">
                          <ArrowUpRight className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="font-medium leading-relaxed text-slate-700">
                          {obj}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {ipo.strengths && ipo.strengths.length > 0 && (
              <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-emerald-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                    <div className="rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 p-2 shadow-md">
                      <CheckCircle2 className="h-5 w-5 text-white" />
                    </div>
                    Company Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <ul className="space-y-3">
                    {ipo.strengths.map((strength: string) => (
                      <li
                        key={`strength-${strength.substring(0, 20)}`}
                        className="group/item flex items-start gap-3 rounded-xl border border-slate-100 bg-gradient-to-r from-slate-50 to-white p-4 text-sm transition-all hover:border-green-300 hover:bg-gradient-to-r hover:from-green-50/50 hover:to-white hover:shadow-sm"
                      >
                        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 shadow-sm">
                          <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="font-medium leading-relaxed text-slate-700">
                          {strength}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {ipo.risks && ipo.risks.length > 0 && (
              <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-transparent to-orange-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                    <div className="rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 p-2 shadow-md">
                      <AlertCircle className="h-5 w-5 text-white" />
                    </div>
                    Risk Factors
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <ul className="space-y-3">
                    {ipo.risks.map((risk: string) => (
                      <li
                        key={`risk-${risk.substring(0, 20)}`}
                        className="group/item flex items-start gap-3 rounded-xl border border-slate-100 bg-gradient-to-r from-slate-50 to-white p-4 text-sm transition-all hover:border-amber-300 hover:bg-gradient-to-r hover:from-amber-50/50 hover:to-white hover:shadow-sm"
                      >
                        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-sm">
                          <AlertCircle className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="font-medium leading-relaxed text-slate-700">
                          {risk}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Enhanced IPO Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0c4a6e]/5 via-transparent to-blue-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                    <div className="rounded-xl bg-gradient-to-br from-[#0c4a6e] to-blue-600 p-2 shadow-md">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    Issue Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <div className="space-y-3">
                    {[
                      { label: "Issue Type", value: ipo.issueType ?? "N/A" },
                      {
                        label: "Face Value",
                        value: `₹${ipo.faceValue} per share`,
                      },
                      { label: "Issue Size", value: ipo.issueSize },
                      {
                        label: "Fresh Issue",
                        value: ipo.freshIssue ?? "N/A",
                      },
                      { label: "OFS", value: ipo.ofs ?? "N/A" },
                      { label: "NSE Symbol", value: ipo.nseSymbol ?? "N/A" },
                      { label: "BSE Symbol", value: ipo.bseSymbol ?? "N/A" },
                      {
                        label: "Listing At",
                        value: Array.isArray(ipo.listingAt)
                          ? ipo.listingAt.join(", ")
                          : "N/A",
                      },
                    ].map(item => (
                      <div
                        key={`issue-detail-${item.label}`}
                        className="group/item flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-3 text-sm transition-all last:border-0 hover:border-slate-200 hover:bg-white hover:shadow-sm"
                      >
                        <span className="font-semibold text-slate-600">
                          {item.label}
                        </span>
                        <span className="max-w-[60%] text-right font-bold text-slate-900">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-transparent to-purple-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <CardHeader className="relative">
                  <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                    <div className="rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-2 shadow-md">
                      <Shield className="h-5 w-5 text-white" />
                    </div>
                    Additional Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <div className="space-y-3">
                    {[
                      { label: "Registrar", value: ipo.registrar },
                      { label: "Lead Manager", value: ipo.leadManager },
                      { label: "BSE Group", value: ipo.bseGroup ?? "N/A" },
                    ].map(item => (
                      <div
                        key={`additional-info-${item.label}`}
                        className="group/item flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-3 text-sm transition-all last:border-0 hover:border-slate-200 hover:bg-white hover:shadow-sm"
                      >
                        <span className="font-semibold text-slate-600">
                          {item.label}
                        </span>
                        <span className="max-w-[60%] text-right font-bold text-slate-900">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Enhanced Market Lot Tab */}
          <TabsContent value="lotsize" className="space-y-6">
            <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0c4a6e]/5 via-transparent to-blue-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <CardHeader className="relative">
                <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                  <div className="rounded-xl bg-gradient-to-br from-[#0c4a6e] to-blue-600 p-2 shadow-md">
                    <Target className="h-5 w-5 text-white" />
                  </div>
                  Market Lot Information
                </CardTitle>
              </CardHeader>
              <CardContent className="relative">
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gradient-to-r from-slate-50 to-white">
                        <th className="p-4 text-left text-sm font-black uppercase tracking-wider text-slate-600">
                          Category
                        </th>
                        <th className="p-4 text-center text-sm font-black uppercase tracking-wider text-slate-600">
                          Lots
                        </th>
                        <th className="p-4 text-center text-sm font-black uppercase tracking-wider text-slate-600">
                          Shares
                        </th>
                        <th className="p-4 text-right text-sm font-black uppercase tracking-wider text-slate-600">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          category: "Retail Minimum",
                          lotSize: 1,
                          shares: ipo.lotSize,
                          amount: ipo.minInvestment,
                          highlight: true,
                        },
                        {
                          category: "Retail Maximum",
                          lotSize: Math.floor(200000 / ipo.minInvestment),
                          shares:
                            Math.floor(200000 / ipo.minInvestment) *
                            ipo.lotSize,
                          amount:
                            Math.floor(200000 / ipo.minInvestment) *
                            ipo.minInvestment,
                          highlight: false,
                        },
                        {
                          category: "S-HNI Minimum",
                          lotSize: Math.ceil(200001 / ipo.minInvestment),
                          shares:
                            Math.ceil(200001 / ipo.minInvestment) * ipo.lotSize,
                          amount:
                            Math.ceil(200001 / ipo.minInvestment) *
                            ipo.minInvestment,
                          highlight: false,
                        },
                        {
                          category: "B-HNI Minimum",
                          lotSize: Math.ceil(1000001 / ipo.minInvestment),
                          shares:
                            Math.ceil(1000001 / ipo.minInvestment) *
                            ipo.lotSize,
                          amount:
                            Math.ceil(1000001 / ipo.minInvestment) *
                            ipo.minInvestment,
                          highlight: false,
                        },
                      ].map(lot => (
                        <tr
                          key={`lot-${lot.category}`}
                          className={`border-b transition-all duration-300 hover:bg-gradient-to-r hover:from-slate-50 hover:to-white ${
                            lot.highlight ? "bg-emerald-50/50" : ""
                          }`}
                        >
                          <td className="p-4 font-semibold text-slate-700">
                            {lot.category}
                          </td>
                          <td className="p-4 text-center font-bold text-slate-900">
                            {lot.lotSize}
                          </td>
                          <td className="p-4 text-center font-bold text-slate-900">
                            {lot.shares.toLocaleString()}
                          </td>
                          <td className="p-4 text-right">
                            <span className="bg-gradient-to-r from-[#0c4a6e] to-blue-600 bg-clip-text text-lg font-black text-transparent">
                              ₹{lot.amount.toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enhanced Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <Card className="group relative overflow-hidden border-0 bg-white/90 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0c4a6e]/5 via-transparent to-blue-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <CardHeader className="relative">
                <CardTitle className="flex items-center gap-2 text-xl font-black text-slate-900">
                  <div className="rounded-xl bg-gradient-to-br from-[#0c4a6e] to-blue-600 p-2 shadow-md">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  IPO Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="relative">
                <div className="grid gap-4 md:grid-cols-2">
                  {[
                    {
                      name: "Red Herring Prospectus (RHP)",
                      link: ipo.rhpLink,
                      icon: FileText,
                      color: "from-blue-500 to-indigo-600",
                      bgColor: "from-blue-50 to-indigo-50",
                    },
                    {
                      name: "Draft RHP (DRHP)",
                      link: ipo.drhpLink,
                      icon: FileText,
                      color: "from-purple-500 to-pink-600",
                      bgColor: "from-purple-50 to-pink-50",
                    },
                  ].map(doc => (
                    <a
                      key={`doc-${doc.name}`}
                      href={doc.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/doc relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/50 p-5 shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-[#0c4a6e]/30 hover:shadow-xl"
                    >
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${doc.bgColor} opacity-0 transition-opacity duration-300 group-hover/doc:opacity-100`}
                      />
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className={`rounded-xl bg-gradient-to-br ${doc.color} p-3 shadow-md transition-transform duration-300 group-hover/doc:rotate-3 group-hover/doc:scale-110`}
                          >
                            <doc.icon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-bold text-slate-900">
                            {doc.name}
                          </span>
                        </div>
                        <div className="rounded-lg bg-slate-100 p-2 transition-all group-hover/doc:scale-110 group-hover/doc:bg-[#0c4a6e]">
                          <ExternalLink className="h-4 w-4 text-slate-600 transition-colors group-hover/doc:text-white" />
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Enhanced Risk Disclaimer */}
        <Card className="group relative overflow-hidden border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50/50 to-amber-50 shadow-lg transition-all duration-500 hover:border-amber-300 hover:shadow-xl">
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-amber-100/20 to-transparent opacity-0 transition-opacity duration-1000 group-hover:opacity-100" />
          <CardContent className="relative p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="mb-2 text-lg font-black text-amber-900">
                  Risk Disclosure
                </h4>
                <p className="text-sm font-medium leading-relaxed text-amber-800">
                  Investment in IPOs involves risk. There is no assurance of
                  allotment or listing gains. Please read all offer-related
                  documents carefully before investing. Past performance is not
                  indicative of future returns. Investment decisions should be
                  based on individual risk appetite and financial goals.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <CommonModal
          isOpen={isOtpOpen}
          onClose={() => setIsOtpOpen(false)}
          width="w-full sm:w-[600px]"
          height="h-[auto]"
          padding="p-2 sm:p-6"
        >
          <OtpVerification
            onClose={() => setIsOtpOpen(false)}
            onSuccess={() => {
              setIsOtpOpen(false);
              window.location.reload();
            }}
          />
        </CommonModal>
      </div>
    </div>
    // </ProductLayout>
  );
};

export default IPODetailsPage;
